import type { BadgeVariant } from "../components/common/Badge";
import type {
  ITeamStatusMember,
  TeamMemberStatus,
} from "../types/teamStatus.types";
import { getInitials, getAvatarGradient } from "./avatarUtils";

export interface TeamStatusChip {
  key: TeamMemberStatus | "all";
  label: string;
  count: number;
  dotClass: string;
}

const STATUS_META: Record<
  TeamMemberStatus,
  { label: string; variant: BadgeVariant; dotClass: string }
> = {
  present: {
    label: "Present",
    variant: "green",
    dotClass: "bg-success",
  },
  break: {
    label: "On Break",
    variant: "orange",
    dotClass: "bg-warning",
  },
  absent: {
    label: "Absent",
    variant: "red",
    dotClass: "bg-error",
  },
  leave: {
    label: "On Leave",
    variant: "blue",
    dotClass: "bg-primary-500",
  },
};

/** Status filter chips with live counts (Figma liveChips row). */
export const buildTeamStatusChips = (
  users: ITeamStatusMember[],
): TeamStatusChip[] => {
  const counts: Record<TeamMemberStatus, number> = {
    present: 0,
    break: 0,
    absent: 0,
    leave: 0,
  };
  users.forEach((u) => {
    counts[u.status] = (counts[u.status] ?? 0) + 1;
  });
  const total = users.length;
  return [
    {
      key: "all",
      label: "All",
      count: total,
      dotClass: "bg-text-tertiary",
    },
    ...(Object.keys(STATUS_META) as TeamMemberStatus[]).map((key) => ({
      key,
      label: STATUS_META[key].label,
      count: counts[key],
      dotClass: STATUS_META[key].dotClass,
    })),
  ];
};

/** Badge presentation for one roster status. */
export const getTeamStatusBadge = (
  status: TeamMemberStatus,
): { label: string; variant: BadgeVariant } => ({
  label: STATUS_META[status]?.label ?? status,
  variant: STATUS_META[status]?.variant ?? "gray",
});

/** Display model for one roster row (avatar resolved via shared utils). */
export const toTeamStatusRow = (member: ITeamStatusMember) => ({
  ...member,
  initials: getInitials(member.name || "Employee"),
  avatarBg: getAvatarGradient(member.name || "Employee"),
});

export const filterTeamByStatus = (
  users: ITeamStatusMember[],
  status: TeamMemberStatus | "all",
): ITeamStatusMember[] =>
  status === "all" ? users : users.filter((u) => u.status === status);
